/**
 * The person behind a sign-in: their profile and which side of MySerbisyo they are using.
 * Spec 002.
 *
 * Every account is a client. An account with `app_metadata.company_id` is also a trade person, and
 * moves between the two with `setMode`. The profile row is made by a database trigger when the
 * account is created, so it always exists for a signed-in person.
 */
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import type { Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/use-auth'

export type Mode = 'client' | 'business'

export type Profile = {
  id: string
  display_name: string | null
  mobile: string | null
  language: 'en' | 'fil' | null
  last_mode: Mode
}

export const hasCompany = (session: Session | null) =>
  !!(session?.user?.app_metadata as Record<string, unknown> | undefined)?.company_id

async function fetchProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from('profile').select('id, display_name, mobile, language, last_mode').eq('id', userId).maybeSingle()
  if (error) throw error
  return data as Profile | null
}

export function useProfile() {
  const { user } = useAuth()
  return useQuery({
    queryKey: ['profile', user?.id],
    enabled: !!user,
    queryFn: () => fetchProfile(user!.id),
  })
}

export function useSaveProfile() {
  const qc = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: async (patch: Partial<Pick<Profile, 'display_name' | 'language' | 'last_mode'>>) => {
      const { error } = await supabase.from('profile').update(patch).eq('id', user!.id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['profile'] }),
  })
}

/** Remember the side the person is on, so the next sign-in lands there. Never blocks navigation. */
export async function setMode(mode: Mode): Promise<void> {
  const { data } = await supabase.auth.getSession()
  const uid = data.session?.user.id
  if (!uid) return
  const { error } = await supabase.from('profile').update({ last_mode: mode }).eq('id', uid)
  if (error) console.warn('account: could not save the mode', error.message)
}

export type Landing = '/login' | '/welcome' | '/home' | '/dashboard'

/**
 * Where a person goes after sign-in, or when they open the bare address.
 * - not signed in: the sign-in screen;
 * - no name yet: the name step;
 * - a client only: the client home;
 * - a trade person: where they were last, the business dashboard by default.
 */
export async function landingPath(): Promise<Landing> {
  const { data } = await supabase.auth.getSession()
  const session = data.session
  if (!session) return '/login'
  let profile: Profile | null = null
  try { profile = await fetchProfile(session.user.id) } catch { /* fall through to the defaults */ }
  if (!hasCompany(session)) return profile?.display_name ? '/home' : '/welcome'
  return profile?.last_mode === 'client' ? '/home' : '/dashboard'
}

/** "Maria" from "Maria Santos". */
export const firstNameOf = (name: string | null | undefined) => (name ?? '').trim().split(/\s+/)[0] ?? ''
